<template>
  <b-container>
    <b-row>
      <b-col col-auto cols="3">
        <div class="mt-5">
          <b-button
              class="green-button-transparent mr-3 mb-3 border-0"
              @click="openUploadModal"
          >
            <b-icon
                icon="plus-circle-fill"
                class="mr-2 top--3"
                aria-hidden="true"
                scale="1"
            ></b-icon>
            {{ $t('mediaLibrary.upload') }}
          </b-button>
          <b-nav vertical class="nav-tabs flex-column">
            <b-nav-item
              v-for="filter in fileTypeFilters"
              :key="filter.value"
              :active="fileTypeFilter === filter.value"
              @click="selectFilter(filter.value)"
            >
              {{ filter.text }}
            </b-nav-item>
          </b-nav>
        </div>
      </b-col>
      <b-col class="tab-content pb-5">
        <media-library-content
            class="mt-5"
            :images="libraryImages"
            :loading="libraryLoading"
            :total-pages="totalPages"
            :current-page="currentPage"
            :search-query="librarySearchQuery"
            :date-from="dateFrom"
            :date-to="dateTo"
            @search="onSearchUpdate"
            @clear-search="clearLibrarySearch"
            @date-from-change="onDateFromChange"
            @date-to-change="onDateToChange"
            @clear-dates="clearDates"
            @page-change="fetchLibraryImages(true, $event)"
            @download="downloadImage"
            @delete-image="deleteImage"
        />
      </b-col>
    </b-row>
    <modal-uploading-image
      v-if="groupId"
      :is-sys-admin="isSysAdmin || false"
      :group-id="groupId"
      :image-object="uploadImageObject"
      :type="uploadType"
      :hide-library-tab="true"
      :allow-multiple="true"
      @image-uploaded="onImageUploaded"
    />
    <modal-delete-media-item
      v-if="imageToDelete"
      :image="imageToDelete"
      :is-deleting="isDeleting"
      @delete-media-item="confirmDeleteImage"
      @cancel="cancelDeleteImage"
    />
  </b-container>
</template>

<script>
import { debounce } from 'lodash';
import gql from 'graphql-tag';
import SetShowHeaderInCache from '@/graphQlQueries/mutations/setShowHeaderInCache';
import SetPageTypeInCache from '@/graphQlQueries/mutations/setPageTypeInCache';
import SetRunBreadcrumbQueryInCache from '@/graphQlQueries/mutations/setRunBreadcrumbQueryInCache';
import DeleteImageById from '@/graphQlQueries/mutations/deleteImageById';
import { deleteFileFromAws } from '@/plugins/aws';
import UserDataAndAccess from '@/mixins/userDataAndAccess';

export default {
  name: 'MediaLibrary',
  components: {
    // eslint-disable-next-line import/no-unresolved
    MediaLibraryContent: () => import('@/components/settings/media/MediaLibraryContent.vue'),
    ModalUploadingImage: () => import('@/components/modals/ModalUploadImage.vue'),
    ModalDeleteMediaItem: () => import('@/components/modals/ModalDeleteMediaItem.vue'),
  },
  mixins: [UserDataAndAccess],
  data: () => ({
    pageType: 'media-library',
    libraryImages: [],
    libraryLoading: false,
    currentPage: 1,
    totalPages: 1,
    pageSize: 24,
    librarySearchQuery: '',
    fileTypeFilter: 'all',
    dateFrom: null,
    dateTo: null,
    debouncedFetch: null,
    uploadImageObject: {
      url: null,
      width: null,
      height: null,
      focalpoint: { x: 50, y: 50 },
    },
    isSysAdmin: false,
    imageToDelete: null,
    isDeleting: false,
  }),
  computed: {
    fileTypeFilters() {
      return [
        { value: 'all', text: this.$t('mediaLibrary.filterAll') },
        { value: 'images', text: this.$t('mediaLibrary.filterImages') },
        { value: 'video', text: this.$t('mediaLibrary.filterVideo') },
        { value: 'audio', text: this.$t('mediaLibrary.filterAudio') },
        { value: 'pdf', text: this.$t('mediaLibrary.filterPdf') },
      ];
    },
    uploadType() {
      // Allow all supported file types when uploading from the media library
      return 'all';
    },
  },
  watch: {
    groupId(val) {
      if (val) {
        this.fetchLibraryImages(true, 1);
      }
    },
  },
  created() {
    this.debouncedFetch = debounce(() => {
      this.currentPage = 1;
      this.fetchLibraryImages(true, 1);
    }, 300);

    this.$apollo.mutate({
      mutation: SetPageTypeInCache,
      variables: { type: this.pageType },
    });
    this.$apollo.mutate({
      mutation: SetRunBreadcrumbQueryInCache,
      variables: { runQuery: true },
    });
    this.$apollo.mutate({
      mutation: SetShowHeaderInCache,
      variables: { show: true },
    });
  },
  methods: {
    selectFilter(value) {
      this.fileTypeFilter = value;
      this.currentPage = 1;
      this.fetchLibraryImages(true, 1);
    },
    buildQuery() {
      const varDefs = ['$group_id: Int!', '$limit: Int = 20', '$offset: Int = 0', '$search: String = "%%"'];
      const conditions = [
        '{ group_id: { _eq: $group_id } }',
        '{ name: { _ilike: $search } }',
      ];

      if (this.fileTypeFilter === 'images') {
        conditions.push(`{ _or: [
          { name: { _ilike: "%.jpg" } },
          { name: { _ilike: "%.jpeg" } },
          { name: { _ilike: "%.png" } },
          { name: { _ilike: "%.gif" } },
          { name: { _ilike: "%.svg" } },
          { name: { _ilike: "%.webp" } }
        ]}`);
      } else {
        varDefs.push('$fileExtension: String');
        conditions.push('{ name: { _ilike: $fileExtension } }');
      }

      if (this.dateFrom) {
        varDefs.push('$dateFrom: timestamptz');
        conditions.push('{ created_at: { _gte: $dateFrom } }');
      }
      if (this.dateTo) {
        varDefs.push('$dateTo: timestamptz');
        conditions.push('{ created_at: { _lte: $dateTo } }');
      }

      const where = `_and: [${conditions.join(', ')}]`;

      return gql`
        query getMediaLibrary(${varDefs.join(', ')}) {
          images_aggregate(where: { ${where} }) {
            aggregate { count }
          }
          images(where: { ${where} }, order_by: {created_at: desc}, limit: $limit, offset: $offset) {
            image_id
            name
            url
            author
            metadata
            created_at
          }
        }
      `;
    },
    async fetchLibraryImages(force = false, page = this.currentPage) {
      if (!this.groupId) return;
      this.libraryLoading = true;
      try {
        const offset = (page - 1) * this.pageSize;
        const variables = {
          group_id: this.groupId,
          limit: this.pageSize,
          offset,
        };

        if (this.librarySearchQuery && this.librarySearchQuery.length >= 3) {
          variables.search = `%${this.librarySearchQuery}%`;
        } else {
          variables.search = '%%';
        }

        if (this.fileTypeFilter === 'video') {
          variables.fileExtension = '%.mp4';
        } else if (this.fileTypeFilter === 'audio') {
          variables.fileExtension = '%.mp3';
        } else if (this.fileTypeFilter === 'pdf') {
          variables.fileExtension = '%.pdf';
        } else if (this.fileTypeFilter !== 'images') {
          variables.fileExtension = '%%';
        }

        if (this.dateFrom) {
          variables.dateFrom = this.dateFrom;
        }
        if (this.dateTo) {
          const endDate = new Date(this.dateTo);
          endDate.setDate(endDate.getDate() + 1);
          [variables.dateTo] = endDate.toISOString().split('T');
        }

        const query = this.buildQuery();

        const { data } = await this.$apollo.query({
          query,
          variables,
          fetchPolicy: force ? 'network-only' : 'cache-first',
        });
        this.libraryImages = data.images || [];
        const totalCount = data.images_aggregate?.aggregate?.count || 0;
        this.totalPages = Math.max(1, Math.ceil(totalCount / this.pageSize));
        if (page > this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages;
          await this.fetchLibraryImages(true, this.totalPages);
          return;
        }
        this.currentPage = page;
      } catch (e) {
        this.libraryImages = [];
      }
      this.libraryLoading = false;
    },
    onSearchUpdate(value) {
      this.librarySearchQuery = value;
      if (value.length === 0 || value.length >= 3) {
        this.debouncedFetch();
      }
    },
    clearLibrarySearch() {
      this.librarySearchQuery = '';
      this.debouncedFetch();
    },
    onDateFromChange(value) {
      this.dateFrom = value;
      this.currentPage = 1;
      this.fetchLibraryImages(true, 1);
    },
    onDateToChange(value) {
      this.dateTo = value;
      this.currentPage = 1;
      this.fetchLibraryImages(true, 1);
    },
    clearDates() {
      this.dateFrom = null;
      this.dateTo = null;
      this.currentPage = 1;
      this.fetchLibraryImages(true, 1);
    },
    openUploadModal() {
      this.uploadImageObject = {
        url: null,
        width: null,
        height: null,
        focalpoint: { x: 50, y: 50 },
      };
      this.$root.$emit('bv::show::modal', 'image-upload');
    },
    onImageUploaded() {
      // Refresh the library after upload
      this.fetchLibraryImages(true, 1);
    },
    async downloadImage(img) {
      try {
        const response = await fetch(img.url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = img.name || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
      }
    },
    deleteImage(img) {
      this.imageToDelete = img;
    },
    cancelDeleteImage() {
      if (this.isDeleting) return;
      this.imageToDelete = null;
    },
    async confirmDeleteImage() {
      if (!this.imageToDelete || this.isDeleting) return;
      this.isDeleting = true;
      const img = this.imageToDelete;
      const toastTitle = this.$t('modals.deleteMediaItem.title');
      try {
        await this.$apollo.mutate({
          mutation: DeleteImageById,
          variables: {
            image_id: img.image_id,
          },
        });

        try {
          await deleteFileFromAws(img.url);
        } catch (s3Error) {
          console.error('Failed to delete file from S3:', s3Error);
        }

        await this.fetchLibraryImages(true, this.currentPage);

        this.$bvToast.toast(this.$t('mediaLibrary.deleteSuccess'), {
          title: toastTitle,
          variant: 'success',
          solid: true,
          autoHideDelay: 2500,
        });
      } catch (error) {
        this.$bvToast.toast(this.$t('mediaLibrary.deleteError'), {
          title: toastTitle,
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        });
      } finally {
        this.isDeleting = false;
        this.imageToDelete = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .btn.dot-button {
  color: #b9b9b9;
  background: transparent;
  border: none;
  padding: 5px;

  &:hover {
    color: #fff;
  }
}
</style>
